import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { Breadcrumbs, Page } from 'components';
import usesLegacyServiceProvider from 'utils/usesLegacyAnsweringServiceProvider';
import { AnsweringServiceContextProvider } from './AnsweringServiceContext';
import AnsweringServiceLayout from './AnsweringServiceLayout';
import { StyledLoading } from './StyledComponents';
import AnsweringServiceError from './AnsweringServiceError';
import { ViewingLevel } from '../SubscriptionsPaymentsPage';

/** Breadcrumbs of previous pages to show at the top of the page */
const STATIC_BREADCRUMBS = [
  { breadcrumbName: 'Admin', path: '/admin' },
  { breadcrumbName: 'Live Concierge Service', path: '/answering_service/org' },
];

/**
 * The root page for a user to subscribe to Answering Service
 * (both Appointment Desk and Live Concierge) and to manage which
 * listings have Answering Service if they are already subscribed.
 *
 * AnsweringServiceLayout handles the internal content of the page.
 * There is both a version of the page for Brokerages accessible
 * through the Admin menu and a version for Users accesible through
 * the user menu. Brokers can access both depending which they navigate to.
 */
const AnsweringServicePage: React.FC<{
  /** Managing the Answering Service subscription as a brokerage or as a user. */
  viewingLevel: ViewingLevel;
}> = ({ viewingLevel }) => {
  const {
    auth,
    answeringPortalConfig,
    answeringPortalConfigError,
    loadingAnsweringPortalConfig,
  } = useContext(AuthContext);
  const history = useHistory();
  const { md } = useBreakpoint();
  const [loadingLegacyStatus, setLoadingLegacyStatus] = useState(true);

  useEffect(() => {
    /**
     * Redirect the user to the old legacy Answering Service page
     * if they use a legacy Answering Service provider.
     */
    const redirectIfUsingLegacy = async () => {
      if (
        !loadingAnsweringPortalConfig &&
        (await usesLegacyServiceProvider(answeringPortalConfig, auth.org))
      ) {
        history.replace('/admin/answering');
      } else {
        setLoadingLegacyStatus(false);
      }
    };
    redirectIfUsingLegacy();
  }, [answeringPortalConfig, auth.org, loadingAnsweringPortalConfig, history]);

  let pageContent: JSX.Element | null = null;
  // Set which of the four page contents we will display.
  // Option 1: Show the loading screen as we fetch the Onboarding Portal config and check their Answering Service provider
  if (loadingAnsweringPortalConfig || loadingLegacyStatus) {
    pageContent = StyledLoading;

    // Option 2: Show the error screen for an error fetching the Onboarding Portal config
  } else if (answeringPortalConfigError) {
    pageContent = <AnsweringServiceError />;
  }

  // Option 3: Show the actual Appointment Desk/Live Concierge page
  else {
    pageContent = (
      <AnsweringServiceContextProvider
        orgId={auth.org.id}
        viewingLevel={viewingLevel}
        userId={auth.id}
        billingRedirection={() =>
          history.push(`/subscriptions_payments/${viewingLevel}`)
        }
      >
        <AnsweringServiceLayout />
      </AnsweringServiceContextProvider>
    );
  }

  // Display whichever content we chose
  return viewingLevel === 'org' ? (
    <Page
      overTitle={
        <StyledOverTitle>
          <Breadcrumbs breadcrumbRoutes={STATIC_BREADCRUMBS} />
        </StyledOverTitle>
      }
      title={md ? 'Live Concierge Service' : undefined}
      showBackButton={md}
      goBackUrl="/admin"
      type="hollow"
      extraTitleMargin
    >
      {pageContent}
    </Page>
  ) : (
    pageContent
  );
};

export default AnsweringServicePage;

const StyledOverTitle = styled.div`
  margin-bottom: 10px;
`;
